import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RotateCcw, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import {
  DEFAULT_LEGEND_STYLE, LEGEND_LIMITS, LEGEND_POSITIONS, LEGEND_WEIGHTS, bakedFontPx,
  resolveLegendStyle,
} from "@/lib/legendStyle";
import { tr } from "@/lib/i18n";

/**
 * Settings → Images · Disclosure legend style.
 *
 * Edits the property's `image_legend_style` — the look of the legend
 * burnt into published photos (WordPress featured + body images,
 * Facebook photo posts, sponsored posts). `value` is the resolved style
 * (`settings.image_legend_style_effective`); `onChange(next)` hands the
 * whole object back to SettingsPage, which saves it with Save.
 *
 * The preview posts the UNSAVED style to /settings/image-legend/preview,
 * which draws it with the same Pillow code as publishing, on the
 * latest article photo, a light/dark backdrop, or a test photo.
 */

const labelCls = "text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display";
const inputCls =
  "w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]";

const FALLBACK_FAMILIES = [
  { key: "sans", get label() { return tr("Sans (default)"); } },
  { key: "serif", get label() { return tr("Serif (Liberation Serif)"); } },
  { key: "mono", get label() { return tr("Mono (Liberation Mono)"); } },
];

function familyKey(display) {
  // Same slug rule as the Ad Style Editor / font_manager.font_path_for.
  return String(display || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function Slider({ label, value, min, max, step, onChange, format, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className={labelCls}>{label}</label>
        <span className="text-xs font-mono-display text-[var(--ink-muted)]">
          {format ? format(value) : value}
        </span>
      </div>
      <input
        type="range"
        data-testid={testId}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1"
      />
    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  const hex = /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#000000";
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="color"
          data-testid={`${testId}-picker`}
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 border border-[var(--border)] cursor-pointer p-0"
        />
        <input
          type="text"
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
        />
      </div>
    </div>
  );
}

export default function ImageLegendStyleEditor({ value, onChange }) {
  const st = resolveLegendStyle(value);
  const set = (patch) => onChange({ ...st, ...patch });

  const fontsQ = useQuery({
    queryKey: ["ad-fonts"],
    queryFn: () => api.listFonts(),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const families = useMemo(() => {
    const installed = (fontsQ.data || [])
      .map((f) => ({ key: familyKey(f.family), label: f.family }))
      .filter((f) => !f.key.startsWith("liberation"));
    const seen = new Set(installed.map((f) => f.key));
    const out = [...FALLBACK_FAMILIES.filter((f) => !seen.has(f.key)), ...installed];
    if (!out.some((f) => f.key === st.font_family)) {
      out.push({ key: st.font_family, label: tr("{font_family} (not installed)", { font_family: st.font_family }) });
    }
    return out;
  }, [fontsQ.data, st.font_family]);

  // ---- preview -------------------------------------------------------
  const [text, setText] = useState("Photo courtesy of Ayuntamiento de Ensenada");
  const [sample, setSample] = useState("article");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  // The current object URL, so it can be revoked when replaced and on
  // unmount without a state update.
  const urlRef = useRef("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const styleKey = JSON.stringify(st);

  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      setError("");
      api
        .previewImageLegend({ style: JSON.parse(styleKey), text, sample, file, signal: ctrl.signal })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = url;
          setPreviewUrl(url);
        })
        .catch((e) => {
          if (ctrl.signal.aborted) return;
          setError(e?.response?.status ? tr("Preview failed ({status})", { status: e.response.status }) : tr("Preview failed"));
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false);
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [styleKey, text, sample, file]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  return (
    <div className="space-y-5" data-testid="image-legend-style-editor">
      <p className="text-xs text-[var(--ink-muted)]">{tr("How the disclosure legend (“Photo courtesy of…”, “AI-generated illustration”, a photo credit) looks when it is burnt into published photos: WordPress featured and body images, and Facebook photo posts. Size is a share of the photo’s width, so it looks the same on every photo. On sponsored Facebook posts the legend keeps to the corner opposite the ad. Changes apply to images published after you Save; photos already published keep the legend they were published with.")}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Controls */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{tr("Font")}</label>
              <select
                data-testid="legend-font-family"
                value={st.font_family}
                onChange={(e) => set({ font_family: e.target.value })}
                className={inputCls}
              >
                {families.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>{tr("Style")}</label>
              <select
                data-testid="legend-font-weight"
                value={st.font_weight}
                onChange={(e) => set({ font_weight: e.target.value })}
                className={inputCls}
              >
                {LEGEND_WEIGHTS.map((w) => (
                  <option key={w.key} value={w.key}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Slider
            label={tr("Text size")}
            testId="legend-size"
            value={st.size_pct}
            min={LEGEND_LIMITS.size_pct[0]}
            max={LEGEND_LIMITS.size_pct[1]}
            step={0.1}
            onChange={(v) => set({ size_pct: v })}
            format={(v) => tr("{toFixed}% of width · {bakedFontPx}px on a 1200px photo", { toFixed: v.toFixed(1), bakedFontPx: bakedFontPx(1200, { ...st, size_pct: v }) })}
          />

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="legend-uppercase"
              checked={!!st.uppercase}
              onChange={(e) => set({ uppercase: e.target.checked })}
            />{tr("ALL CAPS")}</label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ColorField
              label={tr("Text colour")}
              testId="legend-text-color"
              value={st.text_color}
              onChange={(v) => set({ text_color: v })}
            />
            <Slider
              label={tr("Text opacity")}
              testId="legend-text-opacity"
              value={st.text_opacity}
              min={0}
              max={100}
              step={1}
              onChange={(v) => set({ text_opacity: v })}
              format={(v) => `${v}%`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ColorField
              label={tr("Background colour")}
              testId="legend-bg-color"
              value={st.bg_color}
              onChange={(v) => set({ bg_color: v })}
            />
            <Slider
              label={tr("Background opacity")}
              testId="legend-bg-opacity"
              value={st.bg_opacity}
              min={0}
              max={100}
              step={1}
              onChange={(v) => set({ bg_opacity: v })}
              format={(v) => (v === 0 ? tr("0% (no background)") : `${v}%`)}
            />
          </div>

          <Slider
            label={tr("Rounded corners")}
            testId="legend-corners"
            value={st.corner_pct}
            min={0}
            max={100}
            step={1}
            onChange={(v) => set({ corner_pct: v })}
            format={(v) => (v === 0 ? tr("Square") : v === 100 ? tr("Full pill") : `${v}%`)}
          />

          <Slider
            label={tr("Padding")}
            testId="legend-padding"
            value={st.padding}
            min={LEGEND_LIMITS.padding[0]}
            max={LEGEND_LIMITS.padding[1]}
            step={0.05}
            onChange={(v) => set({ padding: v })}
            format={(v) => tr("{toFixed} × text size", { toFixed: v.toFixed(2) })}
          />

          <div>
            <label className={labelCls}>{tr("Position")}</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {LEGEND_POSITIONS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`legend-position-${p.key}`}
                  onClick={() => set({ position: p.key })}
                  className={
                    "px-3 py-1.5 text-sm border " +
                    (st.position === p.key
                      ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                      : "border-[var(--border)] hover:border-[var(--ink)]")
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            data-testid="legend-reset"
            onClick={() => onChange({ ...DEFAULT_LEGEND_STYLE })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:border-[var(--ink)]"
          >
            <RotateCcw className="w-3.5 h-3.5" />{" "}{tr("Reset to default look")}</button>
        </div>

        {/* Preview */}
        <div className="space-y-3">
          <div>
            <label className={labelCls}>{tr("Preview text")}</label>
            <input
              data-testid="legend-preview-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className={inputCls}
              placeholder={tr("Photo courtesy of …")}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="legend-preview-sample"
              value={file ? "file" : sample}
              onChange={(e) => {
                if (e.target.value === "file") {
                  fileRef.current?.click();
                  return;
                }
                setFile(null);
                setSample(e.target.value);
              }}
              className="px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            >
              <option value="article">{tr("Latest article photo")}</option>
              <option value="light">{tr("Light background")}</option>
              <option value="dark">{tr("Dark background")}</option>
              <option value="file">{file ? tr("Test photo: {name}", { name: file.name }) : tr("Upload a test photo…")}</option>
            </select>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="legend-preview-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setFile(f);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:border-[var(--ink)]"
            >
              <Upload className="w-3.5 h-3.5" />{" "}{tr("Test photo")}</button>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                title={tr("Stop using the test photo")}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {loading && <Loader2 className="w-4 h-4 animate-spin text-[var(--ink-muted)]" />}
          </div>
          <div className="border border-[var(--border)] bg-[var(--secondary)] min-h-[160px] flex items-center justify-center">
            {previewUrl ? (
              <img
                data-testid="legend-preview-image"
                src={previewUrl}
                alt={tr("Disclosure legend preview")}
                className="w-full h-auto block"
              />
            ) : (
              <span className="text-xs text-[var(--ink-muted)]">
                {error || tr("Rendering preview…")}
              </span>
            )}
          </div>
          {error && previewUrl && <div className="text-xs text-red-600">{error}</div>}
          <p className="text-[11px] text-[var(--ink-muted)]">{tr("Rendered by the same code that burns the legend in at publish time. Nothing is saved until you press Save.")}</p>
        </div>
      </div>
    </div>
  );
}
